// Palette a location (center) can be given on the global locations settings
// page. The colour is the center's visual identity: it colours the line in the
// header switcher and the accent dot on the location cards, so a planner working
// across several centers can tell at a glance which one they are in.
//
// Every entry is token-based (see src/styles/variables.css). `base` is the
// saturated colour used for swatches and accents; `tint` is the light variant
// for use behind dark text — currently unconsumed (the monogram that used it was
// replaced by the colour line) but kept for future light-background uses.

export interface LocationColor {
  id: LocationColorId;
  // CSS custom property for the saturated colour, e.g. accents and swatches.
  base: string;
  // CSS custom property for the light tint, for backgrounds behind dark text.
  // No current consumer; see the header comment.
  tint: string;
  // The literal value of `base`, because the backend stores a location's colour
  // as a hex string rather than a token name (see locationColorService). Kept
  // beside the token it mirrors so the pair cannot be updated one-sidedly; a
  // test asserts it still matches the token's value in variables.css.
  hex: string;
  // Translation key for the swatch's accessible name.
  labelKey: string;
}

export const LOCATION_COLOR_IDS = [
  'blue',
  'terracotta',
  'purple',
  'green',
  'amber',
  'rose'
] as const;

export type LocationColorId = (typeof LOCATION_COLOR_IDS)[number];

// Keyed by id rather than a loose array, so the compiler rejects a palette that
// is missing an id in LOCATION_COLOR_IDS or that defines one twice.
const LOCATION_COLORS_BY_ID: Record<LocationColorId, LocationColor> = {
  blue: {
    id: 'blue',
    base: 'var(--color-graphics-graph-first)',
    tint: 'var(--color-graphics-graph-first-1)',
    hex: '#89A6FB',
    labelKey: 'config-loc-color-blue'
  },
  terracotta: {
    id: 'terracotta',
    base: 'var(--color-graphics-graph-second)',
    tint: 'var(--color-graphics-graph-second-1)',
    hex: '#D48C72',
    labelKey: 'config-loc-color-terracotta'
  },
  purple: {
    id: 'purple',
    base: 'var(--color-graphics-graph-third)',
    tint: 'var(--color-graphics-graph-third-1)',
    hex: '#7871AA',
    labelKey: 'config-loc-color-purple'
  },
  green: {
    id: 'green',
    base: 'var(--color-success-primary)',
    tint: 'var(--color-success-1)',
    hex: '#4DA871',
    labelKey: 'config-loc-color-green'
  },
  amber: {
    id: 'amber',
    base: 'var(--color-warning-primary)',
    tint: 'var(--color-warning-1)',
    hex: '#DDB847',
    labelKey: 'config-loc-color-amber'
  },
  rose: {
    id: 'rose',
    base: 'var(--color-error-primary)',
    tint: 'var(--color-error-1)',
    hex: '#C45A73',
    labelKey: 'config-loc-color-rose'
  }
};

// Swatch order in the picker follows LOCATION_COLOR_IDS.
export const LOCATION_COLOR_PALETTE: LocationColor[] = LOCATION_COLOR_IDS.map(
  (id) => LOCATION_COLORS_BY_ID[id]
);

export const DEFAULT_LOCATION_COLOR: LocationColor = LOCATION_COLORS_BY_ID[LOCATION_COLOR_IDS[0]];

// Membership is checked against the id tuple, not with `in` against the lookup
// object: `in` walks the prototype chain, so keys like "toString" would pass and
// then resolve to an inherited function that defeats the fallback below.
export const isLocationColorId = (value: string): value is LocationColorId =>
  (LOCATION_COLOR_IDS as readonly string[]).includes(value);

// Falls back to the first palette entry so an unknown id (e.g. left over after
// a palette rename) never renders an invalid colour.
export const getLocationColor = (id: string): LocationColor =>
  isLocationColorId(id) ? LOCATION_COLORS_BY_ID[id] : DEFAULT_LOCATION_COLOR;
